import React, { useState, useEffect } from 'react';
import { signIn, getOAuthUrl, completeNewPassword } from '../services/cognito';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import SocialLoginButtons from './Shared/SocialLoginButtons';
import { trackLogin } from '../services/analytics';
import styles from './LoginForm.module.css';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [cognitoUserToUpdate, setCognitoUserToUpdate] = useState<any>(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [missingAttributes, setMissingAttributes] = useState<string[]>([]);
  const [attributeValues, setAttributeValues] = useState<{ [key: string]: string }>({});
  const [isCheckingSSO, setIsCheckingSSO] = useState(false);
  const [ssoMessage, setSSoMessage] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Auto-login if temp_auth exists and user came from signup
  useEffect(() => {
    const tempAuth = sessionStorage.getItem('temp_auth');
    if (tempAuth && searchParams.get('verified') === '1') {
      try {
        const { email: savedEmail, password: savedPassword } = JSON.parse(tempAuth);
        setEmail(savedEmail);
        setPassword(savedPassword);
        const doAutoLogin = async () => {
          try {
            const session: any = await signIn(savedEmail, savedPassword);
            localStorage.setItem('idToken', session.getIdToken().getJwtToken());
            localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
            sessionStorage.removeItem('temp_auth');
            navigate('/dashboard');
            window.location.reload();
          } catch {
            // Let user log in manually
          }
        };
        doAutoLogin();
      } catch {
        // Invalid temp_auth
      }
    }
  }, []);

  // FSP Detection Logic moved up
  const isFSP = window.location.hostname.includes('fsp') ||
    window.location.hostname.includes('food-safety') ||
    window.location.hostname.includes('foodsafety') ||
    (window.location.hostname.includes('azurestaticapps') && document.referrer.includes('fsp'));

  // Check if email domain has SAML configuration
  const checkSAMLConfig = async (emailDomain: string): Promise<boolean> => {
    try {
      const apiUrl = process.env.REACT_APP_LANDLORD_PRO_URL;
      if (!apiUrl) {
        console.warn('[LoginForm] REACT_APP_LANDLORD_PRO_URL not configured');
        return false;
      }
      const response = await fetch(
        `${apiUrl}/auth/saml/config?domain=${encodeURIComponent(emailDomain)}`
      );
      if (!response.ok) return false;
      const data = await response.json();
      return data.hasSAML === true;
    } catch (err) {
      console.error('[LoginForm] SAML config check failed:', err);
      return false;
    }
  };

  // Determine if email is Super Admin (@compliance-engine.io)
  const isSuperAdminEmail = (email: string): boolean => {
    return email.toLowerCase().endsWith('@compliance-engine.io');
  };


  // Direct Cognito login (used for Super Admin and standard users)
  const handleCognitoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signIn(email, password);
      trackLogin('password');
      navigate('/auth/callback');  // Redirect to callback for routing logic
    } catch (err: any) {
      console.error('[Login] Full Error Object:', err);

      // Handle specific Cognito exceptions
      const errorCode = err.code || err.__type || '';
      const errorMessage = err.message || '';

      // CRITICAL FIX: Handle Force Change Password or Not Confirmed
      if (errorCode === 'UserNotConfirmedException' || errorMessage.toLowerCase().includes('not confirmed')) {
        console.log('[Login] User not confirmed, redirecting to confirmation page');
        navigate(`/confirm?email=${encodeURIComponent(email)}`);
        return;
      }

      if (errorCode === 'NewPasswordRequired') {
        setIsNewPasswordRequired(true);
        setCognitoUserToUpdate(err.user);
        setMissingAttributes(err.requiredAttributes || []);
        setError('This is a temporary password. Please set a new password to continue.');
        return;
      }

      // Better error messages for common issues
      if (errorCode === 'NotAuthorizedException') {
        setError('Invalid email or password. Please try again or reset your password.');
      } else if (errorCode === 'UserNotFoundException') {
        setError('No account found with this email.');
      } else if (errorCode === 'InvalidParameterException' && errorMessage.includes('SRP_A')) {
        setError('Login configuration error. Please contact support (SRP error).');
      } else {
        setError(errorMessage || 'Login failed. Please check your internet connection and try again.');
      }
    }
  };

  // Enhanced handleSubmit with SAML routing for councils
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsCheckingSSO(true);
    setSSoMessage('');

    try {
      const domain = email.split('@')[1];

      // Super Admin: bypass SAML, use Cognito directly
      if (isSuperAdminEmail(email)) {
        console.log('[LoginForm] Super Admin email detected, using Cognito auth');
        setSSoMessage('Super Admin Portal');
        setIsCheckingSSO(false);
        await handleCognitoLogin(e);
        return;
      }

      // Check if domain has SAML configuration
      if (domain) {
        console.log(`[LoginForm] Checking SAML config for domain: ${domain}`);
        const hasSAML = await checkSAMLConfig(domain);

        if (hasSAML) {
          console.log(`[LoginForm] SAML config found, redirecting to SAML login`);
          setSSoMessage('Enterprise SSO');
          // Route to SAML login page with domain
          navigate(`/auth/sso/${domain}`);
          return;
        }
      }

      // Default: Standard Cognito login
      console.log('[LoginForm] No SAML config found, using standard Cognito auth');
      setSSoMessage('');
      setIsCheckingSSO(false);
      await handleCognitoLogin(e);
    } catch (err: any) {
      console.error('[LoginForm] Routing error:', err);
      setError('Login configuration error. Please try again.');
      setIsCheckingSSO(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (!cognitoUserToUpdate) {
        setError('Missing user object. Please try logging in again.');
        return;
      }
      // Format attributes as required
      const formattedAttributes = { ...attributeValues };
      // Some attributes might need special formatting if required

      await completeNewPassword(cognitoUserToUpdate, newPasswordValue, formattedAttributes);
      // IMPORTANT: The session returned by completeNewPasswordChallenge does NOT include
      // custom attributes (tenantId, role, etc.). We must do a fresh signIn to get a
      // proper JWT with all custom attributes for correct tenant isolation.
      try {
        await signIn(email, newPasswordValue);
      } catch (reLoginErr: any) {
        console.warn('[Login] Re-login after password change failed, proceeding anyway:', reLoginErr);
      }
      // v2.5.4: notify backend that the user's password was just changed so the
      // renewal-policy clock (passwordChangedAt in USER_REGISTRY) resets.
      // Best-effort — if it fails, the next authenticated request triggers the
      // bootstrap path in passwordAgeGate, which writes NOW anyway.
      try {
        const { accountService } = await import('../services/api');
        await accountService.markPasswordChanged();
      } catch (markErr: any) {
        console.warn('[Login] markPasswordChanged failed (non-fatal):', markErr?.message);
      }
      navigate('/auth/callback'); // complete workflow
    } catch (err: any) {
      console.error('[Login] completeNewPassword error:', err);
      const errorMessage = err.message || err.code || 'Failed to update password. Ensure it meets the required policy.';
      setError(errorMessage);
    }
  };

  /* DEBUG: Log hostname to see what's happening in Azure */
  console.log('[LoginForm] Hostname:', window.location.hostname);




  return (
    <div>
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <div className={styles.logoContainer}>
            <Link to="/" className={styles.logoContainerLink}>
              {isFSP ? (
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-utensils text-white text-lg"></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Food Safety Pro</span>
                </div>
              ) : (
                <img src="/Landlord-Pro-Logo.png" alt="Landlord Pro" className={styles.logo} />
              )}
            </Link>
          </div>
          <div className={styles.card}>
            <div className={styles.formSection}>
              {isNewPasswordRequired ? (
                <form onSubmit={handleNewPasswordSubmit}>
                  <p className="text-gray-700 mb-4 font-semibold text-center">
                    Welcome! Please set a permanent password.
                  </p>

                  <label className={styles.label}>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className={styles.input}
                      type={showPassword ? "text" : "password"}
                      value={newPasswordValue}
                      onChange={(e) => setNewPasswordValue(e.target.value)}
                      placeholder="Enter new password"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    </button>
                  </div>

                  {missingAttributes.includes('name') && (
                    <div style={{ marginTop: '16px' }}>
                      <label className={styles.label}>Full Name</label>
                      <input
                        className={styles.input}
                        type="text"
                        value={attributeValues.name || ''}
                        onChange={(e) => setAttributeValues({ ...attributeValues, name: e.target.value })}
                        placeholder="e.g. John Doe"
                        required
                      />
                    </div>
                  )}

                  {missingAttributes.includes('phone_number') && (
                    <div style={{ marginTop: '16px' }}>
                      <label className={styles.label}>Phone Number</label>
                      <input
                        className={styles.input}
                        type="tel"
                        value={attributeValues.phone_number || ''}
                        onChange={(e) => setAttributeValues({ ...attributeValues, phone_number: e.target.value })}
                        placeholder="e.g. +447000000000"
                        title="Include country code, starting with +"
                        required
                      />
                    </div>
                  )}

                  {error && <p className={styles.error} style={{ marginTop: '16px' }}>{error}</p>}

                  <button type="submit" className={styles.loginButton} style={isFSP ? { backgroundColor: '#ea580c', marginTop: '24px' } : { marginTop: '24px' }}>
                    <span>Update & Login</span>
                  </button>

                  <button type="button" onClick={() => { setIsNewPasswordRequired(false); setCognitoUserToUpdate(null); setError(''); }} className={styles.signupButton}>
                    Cancel
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  <label className={styles.label}>E-mail</label>
                  <input className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />

                  <label className={styles.label}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className={styles.input}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {error && <p className={styles.error}>{error}</p>}
                  {ssoMessage && (
                    <p style={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      marginTop: '12px',
                      marginBottom: '12px',
                      fontWeight: '500'
                    }}>
                      {ssoMessage}
                    </p>
                  )}
                  <button
                    type="submit"
                    className={styles.loginButton}
                    disabled={isCheckingSSO}
                    style={isFSP ? { backgroundColor: '#ea580c' } : {}}
                  >
                    <span>{isCheckingSSO ? 'Checking...' : 'Login'}</span>
                    {!isCheckingSSO && (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    )}
                  </button>

                  <button type="button" onClick={() => navigate('/signup')} className={styles.signupButton}>
                    Sign up
                  </button>


                </form>
              )}
            </div>
            {/* E-58 social sign-in buttons (Google / Microsoft / Apple) — Texas market only */}
            <SocialLoginButtons context="login" />
            <div className={styles.bottomSection}>
              <div className={styles.bottomGrid}>
                <div className={styles.leftAlign}>
                  <button className={styles.utilityButton} onClick={() => navigate('/forgot-password')}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    <span>Forgot Password</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div >
  );
};

export default LoginForm;